package com.kanati.admin.features.reports

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.admin.state.formatMoney
import com.kanati.shared.report.DailySalesReportRow
import com.kanati.shared.report.TopProductReportRow
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Composable
fun ReportsScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    school: SelectedSchoolContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
) {
    if (district != null && school != null) {
        TicketReportsPanel(apiClient, district, school, theme)
        ConcessionOrdersPanel(apiClient, district, school, theme)
    }
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<DailySalesReportRow>>(emptyList()) }
    var fromDate by remember { mutableStateOf("") }
    var toDate by remember { mutableStateOf("") }
    var isRefreshing by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var expandedDate by remember { mutableStateOf<String?>(null) }
    var productRowsByDate by remember { mutableStateOf<Map<String, List<TopProductReportRow>>>(emptyMap()) }
    var productRowsLoadingDate by remember { mutableStateOf<String?>(null) }
    var productRowsErrorByDate by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    fun missingDistrictMessage(): String =
        when {
            districtLoading -> "Loading district context..."
            districtLoadError != null -> "District context could not be loaded. Refresh context and select a district."
            district == null -> "Select a district to view reports."
            else -> "Select a school to view reports."
        }

    suspend fun refreshReports(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
    ) {
        isRefreshing = true
        loadError = null
        rows = emptyList()
        try {
            rows = apiClient.getReports(
                districtId = selectedDistrict.id,
                schoolId = selectedSchool.id,
                from = fromDate,
                to = toDate,
            )
            expandedDate = null
            productRowsByDate = emptyMap()
            productRowsLoadingDate = null
            productRowsErrorByDate = emptyMap()
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load reports."
        } finally {
            isRefreshing = false
        }
    }

    fun loadProductsForDay(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
        date: String,
    ) {
        if (productRowsByDate.containsKey(date) || productRowsLoadingDate == date) {
            return
        }

        productRowsLoadingDate = date
        productRowsErrorByDate = productRowsErrorByDate - date

        scope.launch {
            try {
                val products = apiClient.getTopProductsReport(
                    districtId = selectedDistrict.id,
                    schoolId = selectedSchool.id,
                    from = date,
                    to = date,
                )
                productRowsByDate = productRowsByDate + (date to products)
            } catch (exception: Exception) {
                productRowsErrorByDate = productRowsErrorByDate + (
                    date to (exception.message ?: "Unable to load product details for this day.")
                )
            } finally {
                if (productRowsLoadingDate == date) {
                    productRowsLoadingDate = null
                }
            }
        }
    }

    fun loadReports() {
        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict == null || selectedSchool == null) {
            rows = emptyList()
            loadError = missingDistrictMessage()
            return
        }

        scope.launch {
            refreshReports(selectedDistrict, selectedSchool)
        }
    }

    LaunchedEffect(district?.id, school?.id, districtLoading, districtLoadError) {
        expandedDate = null
        productRowsByDate = emptyMap()
        productRowsLoadingDate = null
        productRowsErrorByDate = emptyMap()
        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict != null && selectedSchool != null) {
            refreshReports(selectedDistrict, selectedSchool)
        } else {
            rows = emptyList()
            loadError = missingDistrictMessage()
        }
    }

    SectionCard(
        title = "Daily sales",
        theme = theme,
        description = "Daily sales totals for ${school?.name ?: "the selected school"}.",
        headerActions = {
            ActionButton(
                label = if (isRefreshing) "Loading..." else "Refresh",
                onClick = ::loadReports,
                theme = theme,
                isPrimary = false,
                isDisabled = isRefreshing || district == null || school == null,
            )
        },
    ) {
        FieldRow {
            TextField(
                theme = theme,
                label = "From",
                value = fromDate,
                placeholder = "2026-03-01",
                onValueChange = { fromDate = it },
            )
            TextField(
                theme = theme,
                label = "To",
                value = toDate,
                placeholder = "2026-03-31",
                onValueChange = { toDate = it },
            )
        }

        loadError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

                if (rows.isNotEmpty() && loadError == null) {
            StatusBanner(
                message = "Open a day to inspect its subtotal, tax, and final total in clearer detail.",
                theme = theme,
            )
        }

        if (rows.isEmpty() && !isRefreshing && loadError == null) {
            StatusBanner(
                message = "No daily totals found for ${district?.name ?: "the selected district"} and the current date filter.",
                theme = theme,
            )
        }

        rows.forEach { row ->
            val isExpanded = expandedDate == row.date

            Div(
                attrs = {
                    onClick {
                        if (isExpanded) {
                            expandedDate = null
                        } else {
                            expandedDate = row.date
                            val selectedDistrict = district
                            val selectedSchool = school
                            if (selectedDistrict != null && selectedSchool != null) {
                                loadProductsForDay(selectedDistrict, selectedSchool, row.date)
                            }
                        }
                    }
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(14.px)
                        property("padding", "16px 18px")
                        property("border-radius", "22px")
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                        )
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "0 14px 28px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                        property("cursor", "pointer")
                        property("transition", "transform 140ms ease, box-shadow 140ms ease")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            property("justify-content", "space-between")
                            property("align-items", "center")
                            property("gap", "12px")
                            property("flex-wrap", "wrap")
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(4.px)
                            }
                        }
                    ) {
                        P(
                            attrs = {
                                style {
                                    margin(0.px)
                                    property("font-weight", "650")
                                }
                            }
                        ) {
                            Text(row.date)
                        }
                        P(
                            attrs = {
                                style {
                                    margin(0.px)
                                    property("color", theme.onSurfaceVariant)
                                    property("font-size", "0.9rem")
                                }
                            }
                        ) {
                            Text(if (isExpanded) "Tap again to collapse details." else "Tap to view the full daily breakdown.")
                        }
                    }

                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                property("align-items", "center")
                                property("gap", "10px")
                                property("flex-wrap", "wrap")
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    property("padding", "8px 12px")
                                    property("border-radius", "999px")
                                    property("background", theme.tertiaryContainer)
                                    property("color", theme.onTertiaryContainer)
                                    property("font-weight", "700")
                                }
                            }
                        ) {
                            Text(formatMoney(row.totalAmount))
                        }

                        Div(
                            attrs = {
                                style {
                                    property("color", theme.onSurfaceVariant)
                                    property("font-size", "0.88rem")
                                    property("font-weight", "600")
                                }
                            }
                        ) {
                            Text(if (isExpanded) "Hide details" else "View details")
                        }
                    }
                }

                if (isExpanded) {
                    DailyReportDetailPanel(
                        row = row,
                        district = district,
                        productRows = productRowsByDate[row.date],
                        isLoadingProducts = productRowsLoadingDate == row.date,
                        productLoadError = productRowsErrorByDate[row.date],
                        theme = theme,
                    )
                }
            }
        }
    }
}

@Composable
private fun DailyReportDetailPanel(
    row: DailySalesReportRow,
    district: SelectedDistrictContext?,
    productRows: List<TopProductReportRow>?,
    isLoadingProducts: Boolean,
    productLoadError: String?,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
                padding(16.px)
                borderRadius(20.px)
                property("background", theme.surface)
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    property("gap", "12px")
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            ReportMetricChip(
                label = "Subtotal",
                value = formatMoney(row.subtotalAmount),
                theme = theme,
            )
            ReportMetricChip(
                label = "Tax",
                value = formatMoney(row.taxAmount),
                theme = theme,
            )
            ReportMetricChip(
                label = "Total",
                value = formatMoney(row.totalAmount),
                theme = theme,
                emphasized = true,
            )
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.92rem")
                    property("line-height", "1.5")
                }
            }
        ) {
            Text(
                buildString {
                    append("Daily totals for ")
                    append(row.date)
                    district?.name?.let {
                        append(" in ")
                        append(it)
                    }
                    district?.timezone?.let {
                        append(". This row is grouped using the district-local day in ")
                        append(it)
                        append(".")
                    }
                }
            )
        }

        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(10.px)
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.82rem")
                        property("font-weight", "700")
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("Products Sold")
            }

            when {
                productLoadError != null -> StatusBanner(
                    message = productLoadError,
                    theme = theme,
                    tone = BannerTone.Error,
                )

                isLoadingProducts -> StatusBanner(
                    message = "Loading sold products for ${row.date}...",
                    theme = theme,
                )

                productRows == null -> StatusBanner(
                    message = "Product details are not loaded yet for this day.",
                    theme = theme,
                )

                productRows.isEmpty() -> StatusBanner(
                    message = "No product-level sales rows were returned for this day.",
                    theme = theme,
                )

                else -> productRows.forEach { productRow ->
                    Div(
                        attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                property("justify-content", "space-between")
                                property("align-items", "center")
                                property("gap", "12px")
                                property("flex-wrap", "wrap")
                                padding(12.px, 14.px)
                                borderRadius(18.px)
                                property("background", theme.surfaceContainerHigh)
                                property("border", "1px solid ${theme.outlineVariant}")
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(4.px)
                                }
                            }
                        ) {
                            P(
                                attrs = {
                                    style {
                                        margin(0.px)
                                        property("font-weight", "650")
                                    }
                                }
                            ) {
                                Text(productRow.productName)
                            }
                            P(
                                attrs = {
                                    style {
                                        margin(0.px)
                                        property("color", theme.onSurfaceVariant)
                                        property("font-size", "0.9rem")
                                    }
                                }
                            ) {
                                Text("${productRow.unitsSold} sold")
                            }
                        }

                        Div(
                            attrs = {
                                style {
                                    property("padding", "8px 12px")
                                    property("border-radius", "999px")
                                    property("background", theme.secondaryContainer)
                                    property("color", theme.onSecondaryContainer)
                                    property("font-weight", "700")
                                }
                            }
                        ) {
                            Text(formatMoney(productRow.grossSalesAmount))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportMetricChip(
    label: String,
    value: String,
    theme: MaterialThemePalette,
    emphasized: Boolean = false,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                padding(12.px, 14.px)
                borderRadius(18.px)
                property(
                    "background",
                    if (emphasized) theme.primaryContainer else theme.surfaceContainerHigh
                )
                property(
                    "color",
                    if (emphasized) theme.onPrimaryContainer else theme.onSurface
                )
                property("border", "1px solid ${theme.outlineVariant}")
                property("min-width", "112px")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.76rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("color", if (emphasized) theme.onPrimaryContainer else theme.onSurfaceVariant)
                }
            }
        ) {
            Text(label)
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "1rem")
                    property("font-weight", "700")
                }
            }
        ) {
            Text(value)
        }
    }
}
